import { css } from 'lit';

// Dark-variant token overrides. Declarations only (no selector) — applied by
// variables.css.mjs under both [data-theme-mode="dark"] and
// @media (prefers-color-scheme: dark). Components consume the semantic surface
// layer + the palette, so overriding here themes the whole DS.
//
// Contrast targets (WCAG): body text ≥4.5:1, large/bold text & UI (borders,
// icons, outlines) ≥3:1 against the surface they sit on (raised #1b2127 / base
// #0f1419). Brand blues are LIGHTENED here so outline/accent uses stay visible
// on dark — the dark navies blend into the background otherwise.
export default css`
  /* Surfaces */
  --surface-base: #181b1f;        /* page - neutral gray, deliberately lighter than near-black (DEV-1166 review feedback) */
  --surface-raised: #1b2127;      /* elevated panels */
  --surface-sunken: #14191e;
  --surface-hover: #232c35;
  --surface-overlay: rgba(0, 0, 0, 0.65);
  --surface-knob: #e7eaee;

  /* Borders — visible, ≥3:1 for prominent (input/control) borders */
  --border-subtle: #3a4651;       /* dividers / subtle outlines */
  --border-strong: #62707e;       /* input + control outlines */

  /* Text */
  --on-surface: #f3f5f7;          /* primary (~16:1) */
  --on-surface-muted: #b8c0c9;    /* secondary (~7:1) */
  --on-surface-subtle: #9099a3;   /* tertiary (~4.6:1) */
  --base-text-color: #f3f5f7;

  /* Gray ramp inverted: light ends (surfaces) → dark; dark ends (text) → light;
     mids raised so gray-3 borders/icons and gray-4/5 text clear the 3:1 / 4.5:1 bars. */
  --color-gray-1: #161b21;        /* lightest surface → darkest */
  --color-gray-2: #1f262d;
  --color-gray-3: #6b7682;        /* borders / icons / dim text — visible (>=3:1 on base) */
  --color-gray-4: #99a2ab;        /* tertiary text/icons */
  --color-gray-5: #aab2bb;
  --color-gray-6: #b8c0c9;        /* muted text */
  --color-gray-7: #cfd5db;
  --color-gray-8: #e3e7ea;
  --color-gray-9: #f3f5f7;        /* darkest text → lightest */
  --color-dark-gray: #f3f5f7;     /* legacy text aliases → light */
  --color-dark-gray-1: #f3f5f7;
  --color-dark-gray-2: #f3f5f7;

  /* Brand blues — lightened for dark. Filled controls keep white labels
     (large/bold ≥3:1); outline/clear/menu text & borders become legible. */
  --color-primary: #4d80e0;
  --color-primary-light: #6f9bf0;
  --color-primary-light-1: #6f9bf0;   /* alert outline "default" border */
  --color-primary-dark: #6292ee;      /* outline/menu/clear TEXT (5.3:1 on raised)
                                         + press fill (white label 3.1:1) */
  --color-secondary: #2f93da;          /* white-on-fill >=3:1 AND outline >=3:1 */
  --color-secondary-light: #4aa6ec;
  --color-secondary-light-1: #4aa6ec;
  --color-secondary-dark: #2580c0;
  --color-hover: #6f9bf0;             /* hover accent / focus shadow */
  --color-menu-hover: #232c35;        /* matches --surface-hover */
  --button-hover-fill: #1a2c47;       /* outline-button hover fill. In light this
                                         falls back to --color-hover (pale tint
                                         under navy text, ~12:1); the same token
                                         here put #6f9bf0 BEHIND primary-dark
                                         text at 1.5:1 - illegible. Dark primary
                                         tint keeps the label at 4.6:1. */

  /* NOTE: --color-primary-light-1 above is deliberately declared ONLY here in
     the dark layer (its light-side consumers each carry their own literal
     fallback). Do not adopt it as a light fill - in light it resolves to the
     consumer's fallback, not to a token. */

  /* DAM accent (--color-app-dam aliases this). #4B0082 deep indigo is 1.3-1.5:1
     on the dark surfaces; #9a6fe0 is 4.7:1 as text on --color-gray-1 and still
     3.7:1 behind a white label on a filled control. */
  --color-prism-indigo-normal: #9a6fe0;
  --color-prism-indigo-light: #8a5fc8;  /* DAM secondary: 3.5:1 UI on raised, 4.6:1 under a white label */
  --color-purple-light: #9a6fe0;      /* upload progress fill: 4.2:1 on the dark track */

  /* Accents with no usable dark form. Text use clears 4.5:1 on --surface-raised;
     filled use keeps a white label at >=3:1 (the same bar the brand blues use). */
  --color-red: #ff5a60;               /* text 5.3:1, white label 3.1:1 */
  --color-red-light: #ff474e;         /* --color-error follows via states: text 4.8:1, white label 3.4:1 */
  --color-app-keyword: #6b8afd;       /* splash tile: 5.9:1 on --surface-base */
  --color-purple: #9b8cf0;            /* curator pill TEXT: 5.8:1 on raised; chip fill behind dark text */

  /* Light tint surfaces -> dark tint equivalents (self-theming text sits on
     these at 9-13:1). */
  --color-secondary-light-2: #1d4a52; /* editor-content web/tag chips */
  --color-secondary-light-3: #243240; /* editor-content web chips */
  --color-prism-red-light: #3a2426;   /* publisher path-tree hover/selected */
  --color-warning-light: #4A3410;     /* transcoding badge: 5.3:1 under the #FF9505 label */

  /* Native form controls (inputs, selects, date pickers, scrollbars) follow
     this, not the token layer - without it they stay UA-light under a dark
     theme. The base light value lives in variables.css.mjs. */
  color-scheme: dark;

  /* Literal --color-white/--color-black are intentionally NOT overridden
     (white text on a filled/brand background stays white). */
`;
